import { PERMISSIONS } from 'shared-permissions';

const { VOD_MANAGE, VOD_APPEARANCES_MANAGE, VOD_STATS, EDIT_USERS_VOD, WORKSPACE_SETTINGS, VOD_INTEGRATIONS } = PERMISSIONS;
import {
  ORG_ON_DEMAND_HOME,
  ORG_ON_DEMAND_VIDEOS,
  ORG_ON_DEMAND_PRODUCTS,
  ORG_ON_DEMAND_DISTRIBUTION_PAGES,
  ORG_USERS,
  ORG_GENERAL_SETTINGS,
  ORG_ON_DEMAND_GET_STARTED,
  ORG_EMBED_STATS,
  ORG_ON_DEMAND_ACTIONS,
  ORG_ON_DEMAND_LINKS,
  ORG_ON_DEMAND_THEMES,
  ORG_INTEGRATIONS,
  ORG_ON_DEMAND_DATA_EXPORT_JOBS,
  ORG_ON_DEMAND_PLAYLISTS,
  ORG_ON_DEMAND_PLACEMENTS,
  ORG_ON_DEMAND_APPEARANCES,
  ORG_ON_DEMAND_UGC,
} from 'constants/routes.js';
import { playerV2Filter } from './utils.js';

const ON_DEMAND_ROUTES = [
  {
    title: 'sidebarComponent.homeTitle',
    icon: 'home',
    route: ORG_ON_DEMAND_HOME,
    requiredFeatures: ['vodHomePageEnabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'homeMenuItem',
    allowSuperAdmin: false,
    exact: true,
    shownInMasquerade: false,
  },
  {
    title: 'sidebarComponent.libraryTitle',
    icon: 'mediaLibrary',
    dataTestId: 'libraryMenuItem',
    requiredFeatures: ['distributionV2Enabled'],
    submenu: [
      {
        title: 'sidebarComponent.videosTitle',
        icon: 'videos',
        route: ORG_ON_DEMAND_VIDEOS,
        requiredFeatures: ['productsNavigationEnabled', 'vodEnabled'],
        requiredPermission: VOD_MANAGE,
        dataTestId: 'videosMenuItem',
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.productsTitle',
        icon: 'bag',
        route: ORG_ON_DEMAND_PRODUCTS,
        requiredFeatures: ['productsNavigationEnabled', 'vodEnabled'],
        requiredPermission: VOD_MANAGE,
        dataTestId: 'productsMenuItem',
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.actionsTitle',
        icon: 'actions',
        route: ORG_ON_DEMAND_ACTIONS,
        requiredFeatures: ['vodActionsEnabled', 'vodEnabled'],
        requiredPermission: VOD_MANAGE,
        dataTestId: 'productsMenuItem',
        allowSuperAdmin: true,
      },
      {
        title: 'Campaign GIFs',
        icon: 'link',
        route: ORG_ON_DEMAND_LINKS,
        requiredFeatures: ['vodEnabled', 'campaignGifsEnabled'],
        requiredPermission: VOD_MANAGE,
        dataTestId: 'campaignLinksMenuItem',
        allowSuperAdmin: true,
      },
    ],
  },
  {
    title: 'sidebarComponent.ugcTitle',
    icon: 'people',
    route: ORG_ON_DEMAND_UGC,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', 'ugcV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'ugcMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.playlistsTitle',
    icon: 'playlist',
    route: ORG_ON_DEMAND_PLAYLISTS,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', 'distributionV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'playlistMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.appearancesTitle',
    icon: 'palette',
    route: ORG_ON_DEMAND_APPEARANCES,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', 'distributionV2Enabled'],
    requiredPermission: VOD_APPEARANCES_MANAGE,
    dataTestId: 'appearanceMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.placementsTitle',
    icon: 'wireframesStack',
    route: ORG_ON_DEMAND_PLACEMENTS,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', 'distributionV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'placementMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.videosTitle',
    icon: 'videos',
    route: ORG_ON_DEMAND_VIDEOS,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', '!distributionV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'videosMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.productsTitle',
    icon: 'bag',
    route: ORG_ON_DEMAND_PRODUCTS,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', '!distributionV2Enabled'],
    requiredPermission: [VOD_MANAGE, VOD_INTEGRATIONS],
    dataTestId: 'productsMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.actionsTitle',
    icon: 'actions',
    route: ORG_ON_DEMAND_ACTIONS,
    requiredFeatures: ['vodActionsEnabled', 'vodEnabled', '!distributionV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'productsMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.distributionsTitle',
    icon: 'playlist',
    route: ORG_ON_DEMAND_DISTRIBUTION_PAGES,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', '!distributionV2Enabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'playlistMenuItem',
    allowSuperAdmin: true,
  },
  {
    title: 'sidebarComponent.statsEmbedTitle',
    icon: 'bulb',
    route: ORG_EMBED_STATS,
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', { name: 'vodStatsEmbed', allowUpsell: true }],
    requiredPermission: VOD_STATS,
    dataTestId: 'statsEmbedMenuItem',
    allowSuperAdmin: true,
    upsellFeatureName: 'vodStatsEmbed',
    customFilterFunction: ({ features }) => !features.dataExport?.enabled && !features.dataExport?.canUpsell,
  },
  {
    title: 'sidebarComponent.statsTitle',
    icon: 'insights',
    dataTestId: 'statsMenuItem',
    submenu: [
      {
        title: 'sidebarComponent.statsEmbedTitle',
        route: ORG_EMBED_STATS,
        requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', { name: 'vodStatsEmbed', allowUpsell: true }],
        allowSuperAdmin: true,
        requiredPermission: VOD_STATS,
        dataTestId: 'statsEmbedMenuItem',
        flair: {
          id: 'sidebarFlair-sub-menu-insights',
        },
        upsellFeatureName: 'vodStatsEmbed',
      },
      {
        title: 'sidebarComponent.dataExportJobsTitle',
        route: ORG_ON_DEMAND_DATA_EXPORT_JOBS,
        requiredFeatures: ['vodEnabled', { name: 'dataExport', allowUpsell: true }],
        allowSuperAdmin: true,
        requiredPermission: VOD_STATS,
        upsellFeatureName: 'dataExport',
      },
    ],
    customFilterFunction: ({ features }) => features.dataExport?.enabled || features.dataExport?.canUpsell,
  },
  {
    title: 'sidebarComponent.settingsTitle',
    icon: 'settings',
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled'],
    requiredPermission: VOD_MANAGE,
    dataTestId: 'settingsMenuItem',
    allowSuperAdmin: true,
    align: 'bottom',
    submenu: [
      {
        title: 'sidebarComponent.generalSettingsTitle',
        route: ORG_GENERAL_SETTINGS,
        requiredFeatures: [],
        requiredPermission: WORKSPACE_SETTINGS,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.usersSettingsTitle',
        route: ORG_USERS,
        requiredFeatures: [],
        requiredPermission: EDIT_USERS_VOD,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.themingTitle',
        route: ORG_ON_DEMAND_THEMES,
        requiredFeatures: ['vodThemingEnabled'],
        requiredPermission: VOD_MANAGE,
        customFilterFunction: playerV2Filter,
        allowSuperAdmin: true,
      },
      {
        title: 'sidebarComponent.appIntegrationsTitle',
        route: ORG_INTEGRATIONS,
        requiredPermission: VOD_INTEGRATIONS,
        allowSuperAdmin: true,
      },
    ],
  },
  {
    title: 'sidebarComponent.gettingStartedTitle',
    icon: 'progress',
    requiredFeatures: ['productsNavigationEnabled', 'vodEnabled', '!distributionV2Enabled'],
    route: ORG_ON_DEMAND_GET_STARTED,
    requiredPermission: VOD_MANAGE,
    dataTestId: 'getStartedMenuItem',
    allowSuperAdmin: true,
  },
];

export default ON_DEMAND_ROUTES;
